import React from "react";
import { Moment } from "moment-timezone";

/* ---------------------------------------------------------------------------
   DayCell: small internal component used to render each day cell.
   It uses inline styles but follows EUI-friendly visual language.
   It shows different states: muted (prev/next month), in-range, start, end, today.
   --------------------------------------------------------------------------- */

interface DayCellProps {
  date: Moment;
  month: number;
  year: number;
  onClick: () => void;
  isStart: boolean | Moment | null;
  isEnd: boolean | Moment | null;
  inRange: boolean;
  isToday: boolean;
  muted?: boolean;
}

export const DayCell: React.FC<DayCellProps> = ({
  date,
  onClick,
  isStart,
  isEnd,
  inRange,
  isToday,
  muted = false,
}) => {
  // compute styles
  const baseStyle: React.CSSProperties = {
    width: 36,
    height: 36,
    border: "none",
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontWeight: 600,
    transition: "background-color 0.12s ease, color 0.12s ease",
    userSelect: "none",
  };

  const primaryColor = "var(--button-bg)"; // selected day
  const lightShade = "var(--range-date-cell)"; // range background, hover
  const mediumShade = "var(--button-bg)"; // borders, inactive days
  const textColor = "var(--text)"; // main text
  const subText = "#9CA3AF"; // disabled dates

  let style = { ...baseStyle };

  if (isStart || isEnd) {
    style = {
      ...style,
      backgroundColor: primaryColor,
      color: "var(--toggle-button-text)",
      fontWeight: 400,
    };
  } else if (inRange) {
    style = {
      ...style,
      backgroundColor: lightShade,
      color: primaryColor,
      fontWeight: 400,
    };
  } else if (isToday) {
    style = {
      ...style,
      // backgroundColor: mediumShade,
      // color: "#FFFFFF", //textColor,
      fontWeight: 600,
      border: "2px solid var(--button-bg)",
      color: "var(--button-bg)",
    };
  } else {
    style = {
      ...style,
      backgroundColor: "transparent",
      color: muted ? subText : textColor,
      fontWeight: 400,
    };
  }

  const hoverStyle = { backgroundColor: lightShade };

  return muted ? (
    <button></button>
  ) : (
    <button
      role="gridcell"
      aria-label={date.format("YYYY-MM-DD")}
      onClick={onClick}
      style={style}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        if (!isStart && !isEnd && !inRange && !isToday)
          el.style.backgroundColor = hoverStyle.backgroundColor!;
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        // revert to computed style
        if (!isStart && !isEnd && !inRange && !isToday) el.style.backgroundColor = "transparent";
        if (isToday && !isStart && !isEnd && !inRange) {
          el.style.border = "2px solid var(--button-bg)";
          el.style.color = mediumShade;
        }
      }}>
      <span style={{ pointerEvents: "none" }}>{date.date()}</span>
    </button>
  );
};
